// Billing API surface (apps/app). Wraps /billing/*. docs/replan-v2.md R6′.

import { api } from './api';

export type Currency = 'QAR' | 'AED' | 'SAR' | 'KWD' | 'BHD' | 'OMR' | 'USD';
export type Interval = 'month' | 'year';
// `trialing` is retained ONLY because the Postgres enum still carries it (it
// cannot be dropped). No code path creates it — there are no trials
// (owner ruling 2026-08-25). Treat it as never-entitling.
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
// `refunded` / `partially_refunded` were added to the DB enum by migration 0011
// and are reachable from the invoice list today.
export type InvoiceStatus =
  | 'draft'
  | 'open'
  | 'paid'
  | 'void'
  | 'uncollectible'
  | 'refunded'
  | 'partially_refunded';
export type RefundStatus = 'pending' | 'succeeded' | 'failed';

export interface PlanPrice {
  currency: Currency;
  interval: Interval;
  amountMinor: number;
  displayAmount: string;
}

export interface Plan {
  id: string;
  slug: string;
  audience: 'family' | 'school';
  tier: string;
  nameKey: string;
  taglineKey: string | null;
  features: string[];
  isHighlighted: boolean;
  maxKids: number | null;
  maxTeachers: number | null;
  maxStudents: number | null;
  prices: PlanPrice[];
}

export interface Subscription {
  id: string;
  status: SubscriptionStatus;
  planId: string;
  planNameKey: string;
  currency: Currency;
  interval: Interval;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface Invoice {
  id: string;
  amountMinor: number;
  currency: Currency;
  status: InvoiceStatus;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  /** Present only on the detail read; the list endpoint does not compute it. */
  refundedMinor?: number;
}

/** A stored card as the customer may see it. There is deliberately no token. */
export interface PaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
  // 🔴 Server-computed. Never recompute expiry in the UI from expMonth/expYear —
  // the server owns "what today is" for billing purposes.
  isExpired: boolean;
  isExpiringSoon: boolean;
  createdAt: string;
}

export interface Refund {
  id: string;
  invoiceId: string;
  amountMinor: number;
  currency: Currency;
  reason: string;
  status: RefundStatus;
  providerRef: string | null;
  createdAt: string;
  refundedAt: string | null;
}

/**
 * One invoice plus its settled refund ledger.
 *
 * 🔴 There is deliberately NO `refundableMinor` here. That is an admin concept
 * ("how much may staff still refund?") and surfacing a refundable balance to a
 * customer reads as an offer. See `adminInvoiceDetailDto` in
 * packages/zod-schemas/src/billing.ts.
 */
export interface InvoiceDetail {
  id: string;
  subscriptionId: string;
  amountMinor: number;
  currency: Currency;
  status: InvoiceStatus;
  subtotalMinor: number | null;
  discountMinor: number;
  couponCode: string | null;
  planId: string | null;
  planSlug: string | null;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  providerRef: string | null;
  refunds: Refund[];
  refundedMinor: number;
}

/** Merged money timeline: invoices positive, succeeded refunds negative. */
export interface Transaction {
  id: string;
  kind: 'invoice' | 'refund';
  amountMinor: number;
  currency: Currency;
  status: string;
  description: string;
  occurredAt: string;
  invoiceId: string;
}

export const CURRENCIES: Currency[] = ['QAR', 'AED', 'SAR', 'KWD', 'BHD', 'OMR', 'USD'];

// Best-effort default currency from locale/country (parent can override).
export function defaultCurrency(): Currency {
  return 'QAR';
}

export async function listPlans(
  audience: 'family' | 'school',
  signal?: AbortSignal,
): Promise<Plan[]> {
  const { plans } = await api<{ plans: Plan[] }>(`/billing/plans?audience=${audience}`, { signal });
  return plans;
}

export async function getSubscription(
  signal?: AbortSignal,
): Promise<{ subscription: Subscription | null; invoices: Invoice[] }> {
  return api<{ subscription: Subscription | null; invoices: Invoice[] }>('/billing/subscription', {
    signal,
  });
}

export interface CheckoutResult {
  checkoutUrl: string;
  invoiceId: string;
  subscriptionId: string;
}

export async function startCheckout(input: {
  planId: string;
  currency: Currency;
  interval: Interval;
  couponCode?: string;
}): Promise<CheckoutResult> {
  const { result } = await api<{ result: CheckoutResult }>('/billing/checkout', {
    method: 'POST',
    body: input,
  });
  return result;
}

export async function cancelSubscription(): Promise<void> {
  await api('/billing/cancel', { method: 'POST' });
}

export async function resumeSubscription(): Promise<void> {
  await api('/billing/resume', { method: 'POST' });
}

// ─── M7e: saved cards, invoice detail, money timeline ────────────────────────

export async function listPaymentMethods(signal?: AbortSignal): Promise<PaymentMethod[]> {
  const { paymentMethods } = await api<{ paymentMethods: PaymentMethod[] }>(
    '/billing/payment-methods',
    { signal },
  );
  return paymentMethods;
}

/** Starts the provider's update-card journey; returns the hosted page URL. */
export async function startCardUpdate(): Promise<string> {
  const { checkoutUrl } = await api<{ checkoutUrl: string }>('/billing/payment-methods/update', {
    method: 'POST',
  });
  return checkoutUrl;
}

export async function makeDefaultPaymentMethod(id: string): Promise<void> {
  await api(`/billing/payment-methods/${id}/default`, { method: 'POST' });
}

export async function removePaymentMethod(id: string): Promise<void> {
  await api(`/billing/payment-methods/${id}`, { method: 'DELETE' });
}

export async function getInvoice(id: string, signal?: AbortSignal): Promise<InvoiceDetail> {
  const { invoice } = await api<{ invoice: InvoiceDetail }>(`/billing/invoices/${id}`, { signal });
  return invoice;
}

export async function listTransactions(signal?: AbortSignal): Promise<Transaction[]> {
  const { transactions } = await api<{ transactions: Transaction[] }>('/billing/transactions', {
    signal,
  });
  return transactions;
}
